import React, {
    useCallback,
    useContext,
    useLayoutEffect,
    useRef,
    useState,
    useSyncExternalStore,
} from "react";
import { connectWallet } from "~/api/endpoints";

import connectionStore from "./WalletConnectionStore";
import { noop } from "lodash-es";
import { CreateOrder } from "~/types";

const UXUYWalletContext = React.createContext<{
    isConnecting: boolean;
    setIsConnecting: React.Dispatch<React.SetStateAction<boolean>>;
    getAddress: (chain_id: string) => string | null | undefined;
    getConnectedAddress: () => string | null | undefined;
    getAddressType: (chain_id: string) => number | null | undefined;
    connect: () => Promise<void>;
    connectTo: (chainId: string) => Promise<void>;
    isConnected: () => boolean;
    isConnectedTo: (chainId: string) => boolean;
    disconnect: () => Promise<void>;
    sendTransaction: (chainId: string, tradeOrder: CreateOrder) => Promise<any>;
    waitForTransaction: (chainId: string, hash: string) => Promise<any>;
    supportedChains: string[];
}>(null!);

const UXUYWalletProvider: React.FC<React.PropsWithChildren> = ({
    children,
}) => {
    const [isConnecting, setIsConnecting] = useState(false);
    useSyncExternalStore(
        connectionStore.subscribe,
        connectionStore.getSnapshot
    );

    const reportAddressToServer = useCallback(() => {
        const providers = connectionStore.providers;
        const addresses = providers
            .filter((p) => p.isConnected())
            .map((p) => {
                return {
                    address_type: p.addressType,
                    address: p.address as string,
                };
            });

        if (addresses.length > 0) {
            connectWallet(addresses).catch(noop);
        }
    }, []);

    const reportedRef = useRef(false);
    useLayoutEffect(() => {
        if (reportedRef.current) return;
        reportAddressToServer();
        reportedRef.current = true;
    }, [reportAddressToServer]);

    const connect = useCallback(async () => {
        if (connectionStore.isConnected()) return Promise.resolve();

        if (isConnecting) return;
        setIsConnecting(true);

        try {
            await connectionStore.connect();
            reportAddressToServer();
        } catch (err) {
            console.log(err);
        } finally {
            setIsConnecting(false);
        }
    }, [isConnecting, reportAddressToServer]);

    const connectTo = useCallback(
        async (chainId: string) => {
            if (connectionStore.isConnectedTo(chainId)) return;

            if (isConnecting) return;
            setIsConnecting(true);

            try {
                await connectionStore.connectTo(chainId);
                reportAddressToServer();
            } catch (err) {
                console.log(err);
            } finally {
                setIsConnecting(false);
            }
        },
        [isConnecting, reportAddressToServer]
    );

    const sendTransaction = useCallback(
        async (chainId: string, tradeOrder: CreateOrder) => {
            const provider = connectionStore.findProviderBy(chainId);
            return provider?.sendTransaction(chainId, tradeOrder);
        },
        []
    );

    const waitForTransaction = useCallback(
        async (chainId: string, hash: string) => {
            const provider = connectionStore.findProviderBy(chainId);
            return provider && provider.waitForTransaction(hash);
        },
        []
    );

    const getAddressType = (chainId: string) => {
        const provider = connectionStore.findProviderBy(chainId);
        return provider && provider.addressType;
    };

    const getConnectedAddress = () => {
        if (!connectionStore.isConnected()) return null;
        return connectionStore.providers.find((p) => p.isConnected())?.address;
    };

    return (
        <UXUYWalletContext.Provider
            value={{
                isConnected: connectionStore.isConnected.bind(connectionStore),
                isConnectedTo:
                    connectionStore.isConnectedTo.bind(connectionStore),
                isConnecting,
                setIsConnecting,
                connect,
                connectTo,
                disconnect: connectionStore.disconnect.bind(connectionStore),
                getAddress:
                    connectionStore.getConnectedAddressByChainId.bind(
                        connectionStore
                    ),
                getConnectedAddress,
                supportedChains: connectionStore.supportedChains,
                getAddressType,
                sendTransaction,
                waitForTransaction,
            }}
        >
            {children}
        </UXUYWalletContext.Provider>
    );
};

export default UXUYWalletProvider;

// eslint-disable-next-line react-refresh/only-export-components
export const useUXUYWallet = () => {
    return useContext(UXUYWalletContext);
};
